/**
 * The render projection: confirmed state plus the optimistic overlay.
 *
 * Pure, total and I/O-free, which is what makes it testable without a browser
 * and what makes the optimistic layer's behaviour inspectable. Every question of
 * the form "what does the user see right now?" is answered here and nowhere
 * else, so there is exactly one place to look when the answer is wrong.
 *
 * ## Ordering
 *
 * Base order comes from the cards' fractional `position` values, sorted with
 * `sortByPosition` from `@slate/shared` - the same function the server uses to
 * order a column before computing a midpoint. Sharing it matters: if the client
 * sorted differently from the server, the index a drop computes and the position
 * the server derives from it would disagree, and a card would land one place
 * over from where it was dropped.
 *
 * The overlay works on *ordinal* order, not on positions. A pending move says
 * "put this card at index 3 of that column", which is exactly the intent the
 * drag expressed, and it is what the `card.move` frame carries. Deriving a
 * fractional position locally would mean guessing at a midpoint against a column
 * that a peer may have changed a millisecond ago.
 *
 * ## The drag preview
 *
 * `preview` is applied last, after every pending operation, because it is the
 * most recent statement of intent - the user is holding the card right now.
 *
 * It exists as a parameter rather than as an entry in `pending` for a reason
 * that is worth stating: a drag is not a mutation until it ends. Writing it into
 * the store on every `onDragOver` would produce a mutation per pointer crossing,
 * each one needing its own reconciliation and its own rollback, and a drag that
 * the user cancels with Escape would leave a trail of operations to undo. The
 * preview is local component state; only the drop produces a pending operation.
 */

import type { Card, CardId, Column, ColumnId, Label } from '@slate/shared';
import { sortByPosition } from '@slate/shared';
import type { BoardState, DragPreview } from './types.js';

export interface BoardView {
  columns: Column[];
  cardsByColumn: Map<ColumnId, Card[]>;
  cardById: Map<CardId, Card>;
  /** Cards with at least one unconfirmed operation. The tile renders these with
   * a subdued treatment so the user can tell "saved" from "sending". */
  pendingCards: Set<CardId>;
}

/**
 * Remove a card from whichever column currently holds it.
 *
 * Linear in the number of columns, which is at most a couple of dozen, and
 * avoids maintaining a second index that has to be kept in step with the first.
 */
function detach(order: Map<ColumnId, CardId[]>, cardId: CardId): void {
  for (const ids of order.values()) {
    const index = ids.indexOf(cardId);
    if (index !== -1) {
      ids.splice(index, 1);
      return;
    }
  }
}

function attach(order: Map<ColumnId, CardId[]>, cardId: CardId, columnId: ColumnId, index: number): void {
  // The bucket is created when it is missing rather than the insertion being
  // skipped. That case is reachable: a pending move can name a column that a
  // peer created a moment ago and whose `column.created` frame has not been
  // applied yet. Creating the bucket keeps the card in the model, so it appears
  // the instant the column does - and a column the renderer does not know about
  // simply is not iterated, so the card is briefly invisible rather than briefly
  // in the wrong place. That window is one frame long and self-correcting.
  let ids = order.get(columnId);
  if (ids === undefined) {
    ids = [];
    order.set(columnId, ids);
  }
  const clamped = Math.max(0, Math.min(Math.trunc(index), ids.length));
  ids.splice(clamped, 0, cardId);
}

export function materialise(state: BoardState, preview: DragPreview | null = null): BoardView {
  const columns = sortByPosition(state.columns);
  const cardById = new Map<CardId, Card>(Object.entries(state.cards));

  // Seed the order from the confirmed cards, grouped by column.
  const grouped = new Map<ColumnId, Card[]>();
  for (const column of columns) grouped.set(column.id, []);
  for (const card of cardById.values()) {
    // A card whose column is not in the snapshot is a card that arrived from a
    // peer's frame before the column did. Creating the bucket keeps it visible
    // instead of silently dropping it, and the column's own frame will place it.
    const bucket = grouped.get(card.columnId);
    if (bucket === undefined) grouped.set(card.columnId, [card]);
    else bucket.push(card);
  }

  const order = new Map<ColumnId, CardId[]>();
  for (const [columnId, cards] of grouped) {
    order.set(
      columnId,
      sortByPosition(cards).map((card) => card.id),
    );
  }

  const pendingCards = new Set<CardId>();

  for (const op of state.pending) {
    switch (op.kind) {
      case 'card.create': {
        // An upsert, not an insert. The id is minted by the client and the
        // server accepts it as a hint, so the confirmed snapshot can already
        // hold this card while the operation is still unacknowledged: pending
        // operations deliberately survive a resync, and the reconnect replay
        // re-delivers the client's own `card.created` event. Appending
        // unconditionally in that window would put the id in the order twice,
        // which renders the card twice and hands the sortable two entries with
        // the same id.
        //
        // When the card is already confirmed the confirmed row wins outright,
        // content included. It is the server's own answer, it carries the real
        // `version` that the next edit has to quote, and it reflects any move a
        // peer has made since - none of which the optimistic guess knows about.
        // A pending `card.update` later in the list still layers on top.
        if (!cardById.has(op.card.id)) {
          cardById.set(op.card.id, op.card);
          // A create lands at the end of the column. A create with a target
          // index is not a thing the UI produces: cards are created into a
          // column and then dragged.
          const ids = order.get(op.card.columnId);
          if (ids === undefined) order.set(op.card.columnId, [op.card.id]);
          else ids.push(op.card.id);
        }
        pendingCards.add(op.card.id);
        break;
      }
      case 'card.move': {
        pendingCards.add(op.cardId);
        detach(order, op.cardId);
        attach(order, op.cardId, op.columnId, op.index);
        break;
      }
      case 'card.update': {
        const current = cardById.get(op.cardId);
        if (current === undefined) break;
        pendingCards.add(op.cardId);

        // Shallow merge, and `version` is untouched - see the note in types.ts.
        // `labelIds` is resolved against the board's labels rather than merged,
        // because the patch carries the full intended set, which is what the label
        // picker edits. A `labelIds` the board does not know about is dropped
        // rather than rendered as an empty chip.
        const { labelIds, ...rest } = op.patch;
        const next: Card = { ...current, ...rest };
        if (labelIds !== undefined) {
          const byId = new Map(state.labels.map((label) => [label.id, label]));
          next.labels = labelIds
            .map((id) => byId.get(id))
            .filter((label): label is Label => label !== undefined);
        }
        cardById.set(op.cardId, next);
        break;
      }
      case 'card.archive':
      case 'card.delete': {
        pendingCards.add(op.cardId);
        detach(order, op.cardId);
        // The card object stays in `cardById` so the drawer can still render
        // while an archive is in flight; the view simply stops showing it.
        break;
      }
      case 'column.create': {
        if (!columns.some((column) => column.id === op.column.id)) columns.push(op.column);
        if (!order.has(op.column.id)) order.set(op.column.id, []);
        break;
      }
      case 'column.reorder': {
        const from = columns.findIndex((column) => column.id === op.columnId);
        if (from === -1) break;
        const [moved] = columns.splice(from, 1);
        if (moved === undefined) break;
        columns.splice(Math.max(0, Math.min(op.index, columns.length)), 0, moved);
        break;
      }
      case 'comment.create':
        // Comments live in a separate map that the drawer reads. They have no
        // effect on the board projection.
        break;
    }
  }

  if (preview !== null) {
    detach(order, preview.cardId);
    attach(order, preview.cardId, preview.columnId, preview.index);
  }

  const cardsByColumn = new Map<ColumnId, Card[]>();
  for (const [columnId, ids] of order) {
    const cards: Card[] = [];
    for (const id of ids) {
      const card = cardById.get(id);
      // A defensive skip. An id in the order with no card behind it means the
      // overlay and the confirmed map disagreed, which is a bug - and rendering
      // `undefined` as a card is a worse way to find out about it than a gap.
      if (card !== undefined) cards.push(card);
    }
    cardsByColumn.set(columnId, cards);
  }

  return { columns, cardsByColumn, cardById, pendingCards };
}

/** The index a card currently occupies in its column, or `-1`. Used by the drop
 * resolver and by the keyboard reorder announcements. */
export function indexOfCard(view: BoardView, cardId: CardId): number {
  for (const cards of view.cardsByColumn.values()) {
    const index = cards.findIndex((card) => card.id === cardId);
    if (index !== -1) return index;
  }
  return -1;
}

/** The column a card is rendered in, or `null`. */
export function columnOfCard(view: BoardView, cardId: CardId): ColumnId | null {
  for (const [columnId, cards] of view.cardsByColumn) {
    if (cards.some((card) => card.id === cardId)) return columnId;
  }
  return null;
}
